import BoltIcon from '@mui/icons-material/Bolt';
import SecurityIcon from '@mui/icons-material/Security';
import { Box, Button, Card, CardActions, CardContent, debounce, Dialog, DialogContent, Grid, Typography, Link as MuiLink } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { VerificationAgencyService } from "../../../Component/Api/VerificationAgency/VerificationAgencyService";
import AutocompleteWithInfo from "../../../Component/Common/Components/Autocomplete/AutocompleteWithInfo";
import { IDropDownOptionsWithDescription } from "../../../Component/Common/Components/FormValidationInputs/AppSelectListWithInfo";
import { SmallLoading } from "../../../Component/Common/Components/Loading/IntermediateLoading";
import { MDTypography } from "../../../Component/Common/Components/MDTypography";
import { NoDataScreen } from "../../../Component/Common/Components/NoDataScreen/NoDataScreen";
import { VerificationAgency } from "../../../Component/Model/Dto/VerificationAgency/VerificationAgency";
import colors from "../../../assets/theme/base/colors";
import { UserService } from '../../../Component/Api/User/UserService';
import { DateFormatter, isNullOrEmpty } from '../../../Component/Model/helpers';
import { BEEScorecardService } from '../../../Component/Api/BEEScorecard/BEEScorecard';
import { ScorecardDropdownDto } from '../../../Component/Model/Dto/ScorecardLicense';
import { CustomDialogTitle } from '../../Common/Custom/CustomDialogTitle';
import { ScorecardLicenseModal } from '../../BEEScorecards/ScorecardLicenses/ScorecardLicenseModal/ScorecardLicenseModal';
import { Link } from 'react-router-dom';
import { IntegrationProcessService } from '../../../Component/Api/IntegrationProcess/IntegrationProcessService';
import { SubmitScorecardModal } from '../../BEEScorecards/SubmitScorecardModal';
import { toast } from 'react-toastify';
import { HandshakeOutlined, QuestionAnswer, Update, Warning } from '@mui/icons-material';
import AutocompleteWithIcon, { IDropDownOptionsWithIcon } from '../../../Component/Common/Components/Autocomplete/AutocompleteWithIcon';
import { VerificationHubWelcomeModal } from './VerificationHubWelcomeModal';
import { UserAtom } from '../../../State/user.Atom';
import { useRecoilState } from 'recoil';
const { success, error, primary } = colors;



export const VerificationHubDashboard = () => {

    const [scorecards, SetScorecards] = React.useState<IDropDownOptionsWithDescription[]>([]);
    const [showModal, setShowModal] = React.useState(false);
    const [hasScorecards, setHasScorecards] = React.useState<boolean>(false);
    const [showSubmissionModal, setShowSubmissionModal] = React.useState<boolean>(false);
    const [hasAnyPendingSubmission, setHasAnyPendingSubmission] = React.useState<boolean>(false);
    const [modalKey, setModalKey] = React.useState(0);
    const [currentUser] = useRecoilState(UserAtom);
    const [scorecardName, setScorecardName] = React.useState<string>('');

    const [isLoading, setIsLoading] = React.useState(false);
    const [verificationAgencies, setVerificationAgencies] = React.useState<VerificationAgency.VerificationAgencyLookupDto[]>([]);
    const isFirstLoad = React.useRef(true);

    const { control, setValue, watch } = useForm({
        mode: 'all',
    });

    const verificationAgencyOptions: IDropDownOptionsWithIcon[] = verificationAgencies.map(va => {
        return {
            id: va.id,
            value: `${va.name}`,
            description: va.isPartner ? 'Partnered Agency' : 'Not a Partnered Agency',
            icon: va.isPartner ? <HandshakeOutlined fontSize="medium" style={{ color: success.main }} /> : null,
            showIcon: va.isPartner,
            group: va.isPartner ? 'Partnered Agencies' : 'Non-partnered Agencies'
        };
    });

    const verificationAgency = watch("verificationAgency");
    const scorecard = watch("scorecard");
    const [showIntroModal, setShowIntroModal] = React.useState<boolean>(false);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const verificationAgenciesPromise = VerificationAgencyService.GetVerificationAgencies();
            const userVerificationAgencyPromise = UserService.getVerificationAgency();
            const anyPendingSubmissionPromise = IntegrationProcessService.HasAnyPendingSubmission();
            const [verificationAgenciesResponse, userVerificationAgencyResponse, anyPendingSubmissionResponse] = await Promise.all([verificationAgenciesPromise, userVerificationAgencyPromise, anyPendingSubmissionPromise]);

            const userVerificationAgencyId = userVerificationAgencyResponse.data;
            const verificationAgenciesData = verificationAgenciesResponse.data;
            const hasAnyPendingSubmission = anyPendingSubmissionResponse.data;

            if (userVerificationAgencyId) {
                const verificationAgencyData = verificationAgenciesData.find(x => x.id === userVerificationAgencyId);
                setValue("verificationAgency", {
                    id: verificationAgencyData?.id,
                    value: verificationAgencyData?.name || "",
                    description: verificationAgencyData?.isPartner ? 'Partnered Agency' : 'Not a Partnered Agency',
                });
            }

            setVerificationAgencies(verificationAgenciesData);
            setHasAnyPendingSubmission(hasAnyPendingSubmission);

        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const fetchScorecard = async (searchValue?: string) => {
        try {
            const response = await BEEScorecardService.getScorecardsAvailableForVerification(searchValue);

            if (!searchValue && response.data.length !== 0) {
                setHasScorecards(true);
            }

            const formattedScorecards = response.data.map((scorecard: ScorecardDropdownDto) => {
                const startDate = DateFormatter(scorecard.startDate.toString())
                const endDate = DateFormatter(scorecard.endDate.toString())
                const date = `${scorecard.scorecardType} | ${startDate} - ${endDate}`;

                return {
                    id: scorecard.id,
                    value: scorecard.scorecardName,
                    description: date,
                };
            });

            SetScorecards(formattedScorecards);

            if (isFirstLoad.current && formattedScorecards.length > 0) {
                setValue("scorecard", formattedScorecards[0]);
                isFirstLoad.current = false;
            }
        } catch (error) {
            console.log(error);
        }
    }

    const submitScorecard = async (scorecardId: string) => {
        const hasPendingSubmission = await IntegrationProcessService.HasPendingSubmission(scorecardId);
        if (hasPendingSubmission.data) {
            toast.error("Scorecard is already pending submission");
        }
        else {
            setShowSubmissionModal(true);
        }
    }

    useEffect(() => {
        let verificationHubModalShown = sessionStorage.getItem("verificationHubModalShown");
        setShowIntroModal(!currentUser.hideVerificationHubModal && (verificationHubModalShown === 'false' || verificationHubModalShown == null));
        fetchData();
    }, []);

    useEffect(() => {
        fetchScorecard();
    }, []);

    const closeIntroModal = () => {
        setShowIntroModal(false);
        sessionStorage.setItem("verificationHubModalShown", "true");
    }
    useEffect(() => {
        if (scorecard) {
            setScorecardName(scorecard.value);
        }
    }, [scorecard]);


    const updateVerificationAgency = async (value: any) => {
        try {
            let verificationAgencyId: string | null = null;
            if (!isNullOrEmpty(value)) {
                verificationAgencyId = value.id;
            }
            await VerificationAgencyService.UpdateVerificationAgency(verificationAgencyId);
        } catch (error) {
            console.log(error);
        }
    }

    const debouncedGetScorecards = React.useRef(debounce((value: string) => {
        fetchScorecard(value);
    }, 300));

    const handleInputFieldChange = (value: string) => {
        debouncedGetScorecards.current(value);
    }

    const renderScorecardSection = () => {
        const selectedVerificationAgency = verificationAgencies.find(x => x.id === verificationAgency.id);

        const handleClick = () => {
            submitScorecard(scorecard.id);
        };

        return (
            <>
                {selectedVerificationAgency && !selectedVerificationAgency.isPartner && (

                    <Box
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(255, 255, 255, 0.6)',
                            zIndex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            borderRadius: '16px'
                        }}
                    >
                        <Card sx={{ height: "200px", width: "700px", border: `1px solid ${error.main}`, borderRadius: '16px 0 16px 0' }}>
                            <Box display="flex" flexDirection="row" alignItems="center" sx={{ bgcolor: `${error.main}25`, padding: "10px", borderRadius: '16px 0 0 0' }}>
                                <Warning style={{ width: 40, height: 40, color: `${error.main}` }} />
                                <MDTypography ml={1} variant='body1' sx={{ color: `${error.main}`, fontWeight: 'bold' }}>Restricted Functionality</MDTypography>
                            </Box>
                            <CardContent
                                style={{
                                    padding: "10px",
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%'
                                }}
                            >
                                <MDTypography variant='body2'>
                                    Unfortunately, the benefits of processing your verification within the system are only
                                    available for Verification Agencies that are partnered with the BEE123 software.
                                </MDTypography>
                            </CardContent>
                        </Card>
                    </Box>

                )}

                {scorecard && (
                    <Box display="flex" justifyContent="left">
                        <Card sx={{ boxShadow: 5 }} style={{ height: "225px", width: "400px", padding: "5px", margin: "20px" }}>
                            <CardContent>
                                <MDTypography variant='h5' mt={2} mb={1}>
                                    Submit Data for Verification
                                </MDTypography>
                                <MDTypography variant="body2" mt={2} mb={1}>
                                    Instantly submit all data in your scorecard for verification.
                                </MDTypography>
                            </CardContent>
                            <CardActions style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleClick}
                                >
                                    Submit Scorecard
                                </Button>
                            </CardActions>
                        </Card>
                    </Box>
                )}
            </>
        );
    };

    const closeSubmissionModal = (resetScorecard = false) => {
        setModalKey(prevKey => prevKey + 1);
        setShowSubmissionModal(false);
        if (resetScorecard) {
            setValue("scorecard", null);
        }
    };

    const renderVerificationAgencyContent = useCallback(() => {
        if (!verificationAgency) {
            return (
                <NoDataScreen
                    subText1="Based on the Verification Agency you select, we'll indicate whether you'll benefit from the efficiencies gained by using a Verification that's partnered with the BEE123 software."
                    subText3={
                        <MDTypography variant="body2" color="secondary">
                            <strong>Please select your current Verification Agency.</strong>
                        </MDTypography>
                    }
                    hideHeader={true}
                />
            );
        } else {
            const selectedVerificationAgency = verificationAgencies.find(x => x.id === verificationAgency.id);
            const themeColour = selectedVerificationAgency?.isPartner ? success.main : error.main;
            const text = selectedVerificationAgency?.isPartner ? `is a partnered agency, so you'll benefit from:` : `is not a partnered agency, so unfortunately you won't benefit from:`;

            return (
                <Box style={{ color: themeColour }}>
                    <Typography variant="body1" mb={2}><strong>{selectedVerificationAgency?.name}</strong> {text}</Typography>
                    <Grid container spacing={4} paddingTop={2} justifyContent="flex-start">
                        <Grid item xs={12} sm={6} md={4} lg={3} style={{ minWidth: "300px" }}>
                            <Card sx={{ height: "200px", width: "100%", border: `1px solid ${themeColour}`, borderRadius: '16px 0 16px 0' }}>
                                <Box display="flex" flexDirection="row" alignItems="center" sx={{ bgcolor: `${themeColour}25`, padding: "10px", borderRadius: '16px 0 0 0' }}>
                                    <BoltIcon style={{ width: 40, height: 40, color: `${themeColour}` }} />
                                    <MDTypography ml={1} variant='body1' sx={{ color: `${themeColour}`, fontWeight: 'bold' }}>Instant Data Submission</MDTypography>
                                </Box>
                                <CardContent style={{ padding: "10px" }}>
                                    <MDTypography variant="body2">
                                        Submit your data at the click of a button. No need to export data nor to complete complex Excel data gathering sheets.
                                    </MDTypography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={3} style={{ minWidth: "300px" }}>
                            <Card sx={{ height: "200px", width: "100%", border: `1px solid ${themeColour}`, borderRadius: '16px 0 16px 0' }}>
                                <Box display="flex" flexDirection="row" alignItems="center" sx={{ bgcolor: `${themeColour}25`, padding: "10px", borderRadius: '16px 0 0 0' }}>
                                    <SecurityIcon style={{ width: 40, height: 40, color: `${themeColour}` }} />
                                    <MDTypography ml={1} variant='body1' sx={{ color: `${themeColour}`, fontWeight: 'bold' }}>Enhanced Security</MDTypography>
                                </Box>
                                <CardContent style={{ padding: "10px" }}>
                                    <MDTypography variant="body2">
                                        Ensure data privacy and POPI compliance. Your sensitive data never leaves the secure environment of the platform.
                                    </MDTypography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={3} style={{ minWidth: "300px" }}>
                            <Card sx={{ height: "200px", width: "100%", border: `1px solid ${themeColour}`, borderRadius: '16px 0 16px 0' }}>
                                <Box display="flex" flexDirection="row" alignItems="center" sx={{ bgcolor: `${themeColour}25`, padding: "10px", borderRadius: '16px 0 0 0' }}>
                                    <QuestionAnswer style={{ width: 40, height: 40, color: `${themeColour}` }} />
                                    <MDTypography ml={1} variant='body1' sx={{ color: `${themeColour}`, fontWeight: 'bold' }}>Seamless Communication</MDTypography>
                                </Box>
                                <CardContent style={{ padding: "10px" }}>
                                    <MDTypography variant="body2">
                                        Facilitate smooth collaboration. Communicate directly with your agency and your team to ensure timely task completion.
                                    </MDTypography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={3} style={{ minWidth: "300px" }}>
                            <Card sx={{ height: "200px", width: "100%", border: `1px solid ${themeColour}`, borderRadius: '16px 0 16px 0' }}>
                                <Box display="flex" flexDirection="row" alignItems="center" sx={{ bgcolor: `${themeColour}25`, padding: "10px", borderRadius: '16px 0 0 0' }}>
                                    <Update style={{ width: 40, height: 40, color: `${themeColour}` }} />
                                    <MDTypography ml={1} variant='body1' sx={{ color: `${themeColour}`, fontWeight: 'bold' }}>Real-time Progress Tracking</MDTypography>
                                </Box>
                                <CardContent style={{ padding: "10px" }}>
                                    <MDTypography variant="body2">
                                        Know your verification process status at any point in time with our verification progress tracker.
                                    </MDTypography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>


            )
        }
    }, [verificationAgency, verificationAgencies]);

    return (
        <>
            <Grid container alignItems="center">
                <Grid item xs={10}>
                    <MDTypography variant='h4' fontWeight='bold' style={{ paddingBottom: "10px" }}>
                        B-BBEE Verification Hub
                    </MDTypography>
                </Grid>
                <Grid item xs={2} style={{ textAlign: 'right' }}>
                    <MuiLink onClick={() => { setShowIntroModal(true); }} style={{ cursor: 'pointer' }}>
                        <MDTypography variant="subtitle2"  style={{ textDecoration: 'underline' , color: primary.main}}>
                            How it works
                        </MDTypography>
                    </MuiLink>
                </Grid>
            </Grid>

            <>
                <Card sx={{ marginBottom: '20px' }}>
                    {isLoading && <SmallLoading />}
                    {!isLoading &&
                        <Grid container spacing={2} padding={2}>
                            <MDTypography variant='h5' style={{ padding: "16px" }}>
                                Select your Verification Agency
                            </MDTypography>
                            <Grid container item xs={12} spacing={2} alignItems="center">
                                <Grid item xs={6}>
                                    <AutocompleteWithIcon
                                        control={control}
                                        name="verificationAgency"
                                        label="Current Verification Agency"
                                        items={verificationAgencyOptions}
                                        size="small"
                                        onChange={updateVerificationAgency}
                                        preSelectedItemId={verificationAgency?.id}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MDTypography variant='body2'>
                                        The Verification Agency selected will determine the verification proccess to follow based on whether they're partnered with the BEE123 software.
                                    </MDTypography>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} marginTop={2}>
                                {renderVerificationAgencyContent()}
                            </Grid>
                        </Grid>
                    }
                </Card>
            </>
            {verificationAgency &&
                <>

                    <Card>
                        <Grid container spacing={2} padding={2}>
                            <MDTypography variant='h5' style={{ padding: "16px" }}>
                                Process your Verification
                            </MDTypography>
                            <Grid container item xs={12} spacing={2} alignItems="center">
                                <Grid item xs={6}>
                                    <AutocompleteWithInfo
                                        key={modalKey}
                                        control={control}
                                        name="scorecard"
                                        label="Scorecard for Verification"
                                        items={scorecards}
                                        size="small"
                                        useManualFiltering={true}
                                        style={{ width: "100%" }}
                                        disabled={scorecards.length === 0 && !hasScorecards}
                                        onInputFieldChange={handleInputFieldChange}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <MDTypography variant='body2'>
                                        Select the scorecard containing the data you want to verify.
                                        <br />
                                        <strong>Note</strong> that previously submitted scorecards can be found under the Verification Scorecards tab.
                                    </MDTypography>
                                </Grid>
                            </Grid>

                            {renderScorecardSection()}

                            {!scorecard && <Grid item xs={12} marginTop={2}>
                                {hasScorecards ? (
                                    <>
                                        <NoDataScreen
                                            subText1="We'll show you applicable verification actions once you've selected the scorecard that you'd like to verify."
                                            subText3={
                                                <MDTypography variant="body2" color="secondary">
                                                    <strong>Please select your scorecard.</strong>
                                                </MDTypography>
                                            }
                                            hideHeader={true}
                                        />
                                    </>
                                ) :
                                    (
                                        <>
                                            <NoDataScreen
                                                subText1="You don't have any scorecards that can be submitted for verification."
                                                subText3={
                                                    hasAnyPendingSubmission ? (
                                                        <>
                                                            <MDTypography
                                                                variant="body2"
                                                                color="primary"
                                                                component="span"
                                                                onClick={() => setShowModal(true)}
                                                                style={{ cursor: "pointer", textDecoration: "underline" }}
                                                            >
                                                                Create a scorecard
                                                            </MDTypography>
                                                            <MDTypography variant="body2" color="secondary">
                                                                OR
                                                            </MDTypography>
                                                            <Link to="/bee/verificationScorecards/Open">
                                                                <MDTypography
                                                                    variant="body2"
                                                                    color="primary"
                                                                    component="span"
                                                                    style={{ cursor: "pointer", textDecoration: "underline" }}
                                                                >
                                                                    <p>View scorecards submitted for verification</p>
                                                                </MDTypography>
                                                            </Link>
                                                        </>
                                                    ) : (
                                                        <MDTypography
                                                            variant="body2"
                                                            color="primary"
                                                            component="span"
                                                            onClick={() => setShowModal(true)}
                                                            style={{ cursor: "pointer", textDecoration: "underline" }}
                                                        >
                                                            Create a scorecard
                                                        </MDTypography>
                                                    )
                                                }
                                                hideHeader={true}
                                            />
                                        </>
                                    )
                                }
                            </Grid>
                            }
                        </Grid>
                    </Card>
                </>
            }

            <Dialog
                open={showModal}
                onClose={() => setShowModal(false)}
                aria-labelledby="form-dialog-title"
                maxWidth="xl"
            >
                <CustomDialogTitle
                    id="customized-dialog-title"
                    onClose={() => setShowModal(false)}
                >
                    Add Scorecard
                </CustomDialogTitle>
                <DialogContent>
                    <ScorecardLicenseModal modalDescription={"Summary of Scorecard Licenses"} />
                </DialogContent>
            </Dialog>

            <VerificationHubWelcomeModal
                showIntroModal={showIntroModal}
                closeIntroModal={closeIntroModal}
            />
            {scorecard && verificationAgency &&
                <SubmitScorecardModal
                    key={modalKey}
                    verificationAgencyName={verificationAgency.value}
                    scorecardName={scorecardName}
                    scorecardId={scorecard.id}
                    showSubmissionModal={showSubmissionModal}
                    closeSubmissionModal={closeSubmissionModal}
                />
            }
        </>

    )
}
